/*
 * PDP collection grids - fills the shells rendered by sections/modus-pdp-collection-grid.liquid.
 *
 * Each [data-modus-pdp-grid] shell is filled from
 *   <data-collection-url>?section_id=modus-pdp-collection-grid[&filter.v.option.size=<data-size>]
 * which renders the first 6 cards of that collection (size-filtered when data-size is set) in
 * collection context. The current product's card is dropped and the rest trimmed to data-limit.
 *
 * In stock first (JR 2026-10-21): each set is fetched with &filter.v.availability=1 and topped up
 * from &filter.v.availability=0 only when it has fewer than data-limit cards. Availability is
 * Shopify's "can be bought now", so "Expected <date>" items count as out of stock.
 *
 * Empty size-filtered result (JR 2026-10-21): fall back to the unfiltered collection, hide the
 * [size] word in the heading and point View all at the plain collection URL. Every later size
 * change tries the filtered fetch first again. The shell stays hidden only when the unfiltered
 * result is empty too (or a fetch fails).
 *
 * Size-limited grids (data-size-position) follow the size picker. Empire dispatches variant-change
 * on <variant-selection> without bubbles (empire.js:366), so listen in the capture phase on
 * document, as modus-product-specs.js does.
 *
 * Card behaviour (compare, pro quick add, swatches, lazy images) comes from Empire itself. The
 * cards block carries data-section-type="dynamic-featured-collection", and the bubbling
 * shopify:section:unload / :load events below run Empire's Sections manager (empire.js:25185) and
 * the rimg watcher (empire.js:24997). This is the same pattern collection-ajax-filters.js uses.
 * Never edit empire.js.
 */
(function () {
  if (window.__modusPdpGrids) return; // the section prints this tag once per grid
  window.__modusPdpGrids = true;

  var SECTION = 'modus-pdp-collection-grid';
  var cache = {};

  // avail: '1' in stock, '0' out of stock, omitted = both (the View all link never carries it).
  function collectionUrl(root, withSection, withSize, avail) {
    var u = new URL(root.getAttribute('data-collection-url'), location.origin);
    if (withSection) u.searchParams.set('section_id', SECTION);
    var size = withSize ? root.getAttribute('data-size') : '';
    if (size) u.searchParams.set('filter.v.option.size', size); // encodes as Shopify does: Twin+%283%2F3%29
    if (avail) u.searchParams.set('filter.v.availability', avail);
    return u.pathname + u.search;
  }

  function get(url, signal) {
    if (Object.prototype.hasOwnProperty.call(cache, url)) return Promise.resolve(cache[url]);
    return fetch(url, { credentials: 'same-origin', signal: signal })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        cache[url] = html;
        return html;
      });
  }

  function sectionEvent(el, type, id) {
    // detail is destructured unconditionally in empire.js; listeners live on document, so bubble.
    el.dispatchEvent(new CustomEvent(type, {
      bubbles: true,
      cancelable: false,
      detail: { sectionId: id, blockId: null, load: true }
    }));
  }

  function cardsId(root) {
    return root.getAttribute('data-grid-id') + '--cards';
  }

  function itemPath(item) {
    try {
      return new URL(item.getAttribute('data-product-quickshop-url') || '', location.origin).pathname;
    } catch (e) {
      return '';
    }
  }

  // Parse a cards response and drop the current product's card.
  // Returns { cards: detached cards block, row: its [data-content], items: remaining cards } or null.
  function parse(root, html) {
    var t = document.createElement('template');
    t.innerHTML = html;
    var cards = t.content.querySelector('[data-modus-pdp-grid-cards]');
    var row = cards && cards.querySelector('[data-content]');
    if (!row) return null;

    // data-modus-* on purpose: Empire's recently viewed prepends "x ago" to every
    // [data-product-handle] on the page (empire.js:42735).
    var self = '/products/' + root.getAttribute('data-modus-product-handle');
    var items = [];
    var found = row.querySelectorAll('[data-product-item]');
    for (var i = 0; i < found.length; i++) {
      if (itemPath(found[i]).endsWith(self)) found[i].parentNode.removeChild(found[i]);
      else items.push(found[i]);
    }
    return { cards: cards, row: row, items: items };
  }

  // One set of cards (size-filtered or not), in stock first (JR 2026-10-21): the in-stock fetch,
  // topped up from the out-of-stock fetch only when it falls short of data-limit. Both keep the
  // collection's own sort. A product with in-stock and out-of-stock variants can be in both
  // responses, so the top-up skips cards already shown. Resolves to the cards block, or null.
  function fill(root, sized, signal) {
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 5;
    return get(collectionUrl(root, true, sized, '1'), signal).then(function (inHtml) {
      var set = parse(root, inHtml);
      if (!set) return null;
      if (set.items.length >= limit) return trim(set, limit);
      return get(collectionUrl(root, true, sized, '0'), signal).then(function (outHtml) {
        var more = parse(root, outHtml);
        var seen = {};
        set.items.forEach(function (it) { seen[itemPath(it)] = true; });
        if (more) {
          more.items.forEach(function (it) {
            var p = itemPath(it);
            if (set.items.length >= limit || seen[p]) return;
            seen[p] = true;
            set.row.appendChild(it);
            set.items.push(it);
          });
        }
        return trim(set, limit);
      });
    });
  }

  function trim(set, limit) {
    for (var i = limit; i < set.items.length; i++) set.items[i].parentNode.removeChild(set.items[i]);
    return set.items.length ? set.cards : null;
  }

  function clear(root) {
    var slot = root.querySelector('[data-modus-pdp-grid-slot]');
    var old = slot.querySelector('[data-modus-pdp-grid-cards]');
    if (old) sectionEvent(old, 'shopify:section:unload', cardsId(root));
    slot.innerHTML = '';
    return slot;
  }

  function hide(root) {
    clear(root);
    root.hidden = true;
  }

  // sized: the cards are the size-filtered set (false = the unfiltered fallback or no size at all).
  function show(root, cards, sized) {
    var id = cardsId(root);
    var slot = clear(root);
    var typeEl = cards.querySelector('[data-section-type]');
    if (typeEl) typeEl.setAttribute('data-section-id', id);
    slot.appendChild(cards);

    var link = root.querySelector('[data-modus-pdp-grid-view-all]');
    if (link) link.setAttribute('href', collectionUrl(root, false, sized));
    var word = root.querySelector('[data-modus-pdp-grid-size]');
    if (word) word.hidden = !sized;

    root.hidden = false;
    sectionEvent(cards, 'shopify:section:load', id);
  }

  function load(root) {
    if (root._modusCtrl) root._modusCtrl.abort();
    var ctrl = root._modusCtrl = new AbortController();
    var current = function () { return root._modusCtrl === ctrl; };
    var sized = !!root.getAttribute('data-size');

    fill(root, sized, ctrl.signal)
      .then(function (cards) {
        if (!current()) return;
        if (cards) { show(root, cards, sized); return; }
        if (!sized) { hide(root); return; }
        // Nothing else in this size: show the whole collection, without the size word or filter.
        return fill(root, false, ctrl.signal).then(function (all) {
          if (!current()) return;
          if (all) show(root, all, false);
          else hide(root);
        });
      })
      .catch(function (e) {
        if (e && e.name === 'AbortError') return;
        if (current()) hide(root);
      });
  }

  document.addEventListener('variant-change', function (e) {
    var d = e.detail || {};
    if (!d.variant || !d.product) return; // not-selected / unavailable: keep the last size
    var roots = document.querySelectorAll('[data-modus-pdp-grid][data-size-position][data-modus-product-id="' + d.product.id + '"]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var size = d.variant['option' + root.getAttribute('data-size-position')];
      if (!size || size === root.getAttribute('data-size')) continue;
      root.setAttribute('data-size', size);
      var word = root.querySelector('[data-modus-pdp-grid-size]');
      if (word) word.textContent = size.split(' (')[0].trim();
      load(root); // show() sets the View all href and the size word's visibility
    }
  }, true);

  // Theme editor re-render of a grid section (ignore the events this file dispatches on cards).
  document.addEventListener('shopify:section:load', function (e) {
    var t = e.target;
    if (!t || !t.querySelector || (t.matches && t.matches('[data-modus-pdp-grid-cards]'))) return;
    var root = t.querySelector('[data-modus-pdp-grid]');
    if (root) load(root);
  });

  function init() {
    var roots = document.querySelectorAll('[data-modus-pdp-grid]');
    for (var i = 0; i < roots.length; i++) load(roots[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
